import { useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { MapPin } from 'lucide-react'
import { getRegion, regionForFreeText } from '@shared/regions'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import type { RequestRegionSource } from '../hooks/useRegionRequests'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: RequestRegionSource
  onSubmit: (text: string, source: RequestRegionSource) => Promise<void>
  /** Switches to an area we already cover, when the typed city resolves to one. */
  onPickCovered: (regionId: string) => void
}

export function RequestRegionDialog({
  open,
  onOpenChange,
  source,
  onSubmit,
  onPickCovered,
}: Props) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const valid = text.trim().length > 0

  // The picker searches region names and their city lists, so a user only gets
  // here after failing to find their city — but the free-text matcher knows
  // scene aliases ("SoCal") and `city, ST` forms that search doesn't. Offering
  // the match beats filing a request for an area we already cover.
  const covered = useMemo(() => getRegion(regionForFreeText(text)), [text])

  function reset() {
    setText('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!valid || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(text, source)
      toast.success(
        fbt(
          "Thanks! We'll use this to pick the next area we cover.",
          'Toast after successfully requesting a new region',
        ),
      )
      reset()
      onOpenChange(false)
    } catch {
      // runMutation already surfaced the error toast.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Dialog title for asking Beacon to cover a new area">
              Request your area
            </fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Dialog subtitle explaining what happens to a region request">
              Tell us where you are and we'll use it to decide which area to add next.
            </fbt>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            data-testid="region-request-city"
            placeholder={fbt(
              'City or metro area',
              'Placeholder for the city field when requesting a new region',
            ).toString()}
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={120}
          />

          {covered && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
              <p className="text-sm text-muted-foreground">
                <fbt desc="Hint shown when a requested city already belongs to a covered region">
                  We already cover that as{' '}
                  <fbt:param name="region">{covered.name}</fbt:param>.
                </fbt>
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  onPickCovered(covered.id)
                  reset()
                  onOpenChange(false)
                }}
              >
                <MapPin className="size-3.5" />
                <fbt desc="Button that switches to the covered region matching what the user typed">
                  Show me <fbt:param name="region">{covered.name}</fbt:param>
                </fbt>
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={submitting || !valid}>
              {submitting
                ? fbt('Sending…', 'Button label while a region request is being submitted')
                : fbt('Send request', 'Button label to submit the request-a-region form')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
